import React, { useState, useMemo } from 'react';
import { RecurrencePattern } from '@/types/lead';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Calendar } from '@/components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { TagInput } from '@/components/ui/tag-input';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';
import { createTask, getTaskPriorityOptions, getTaskCategoryOptions } from '@/services/leads/taskService';
import { logActivity } from '@/services/leads/activityService';
import { useToast } from '@/hooks/use-toast';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import RecurrenceSelector from '@/components/tasks/RecurrenceSelector';

interface AddTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddTaskDialog: React.FC<AddTaskDialogProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: companyMembers, isLoading: isLoadingMembers } = useCompanyMembers();

  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [dueTime, setDueTime] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern | null>(null);
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string | null>(null);

  const priorityOptions = getTaskPriorityOptions();
  const categoryOptions = getTaskCategoryOptions();

  // Fetch leads for dropdown - filtered by user
  const { data: leads, isLoading: isLoadingLeads } = useQuery({
    queryKey: ['leads', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        console.warn('No user ID available');
        return [];
      }

      // Fetch only leads created by the current user
      const { data, error } = await supabase
        .from('leads')
        .select('id, name')
        .eq('created_by', user.id)
        .order('name');

      if (error) throw error;
      
      // Deduplicate by name, keeping first occurrence
      const seen = new Set<string>();
      return (data || []).filter(lead => {
        if (seen.has(lead.name)) return false;
        seen.add(lead.name);
        return true;
      });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isOpen
  });

  const resetForm = () => {
    setSelectedLeadId('');
    setTitle('');
    setDescription('');
    setDueDate(undefined);
    setPriority('medium');
    setAssignedTo('');
    setCategories([]);
    setDueTime('');
    setRecurrencePattern(null);
    setRecurrenceInterval(1);
    setRecurrenceEndDate(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedLeadId) {
      toast({
        title: 'Lead Required',
        description: 'Please select a lead for this task',
        variant: 'destructive'
      });
      return;
    }

    if (!title || title.trim().length < 3) {
      toast({
        title: 'Title Required',
        description: 'Task title must be at least 3 characters',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the task
      const newTask = await createTask({
        leadId: selectedLeadId,
        title: title.trim(),
        description: description.trim(),
        dueDate: dueDate ? (() => {
          const d = new Date(dueDate);
          if (dueTime) {
            const [hours, minutes] = dueTime.split(':').map(Number);
            d.setHours(hours, minutes, 0, 0);
          }
          return d.toISOString();
        })() : null,
        priority: priority as 'low' | 'medium' | 'high',
        status: 'pending',
        assignedTo: assignedTo || user?.id || '',
        categories: categories,
        reminderTimes: [],
        recurrencePattern: recurrencePattern,
        recurrenceInterval: recurrenceInterval,
        recurrenceEndDate: recurrenceEndDate,
      });

      // Log activity
      await logActivity({
        leadId: selectedLeadId,
        type: 'task',
        title: 'Task Created',
        description: `Created task: ${title}`,
        metadata: { taskId: newTask.id },
        performedBy: user?.id || ''
      });

      // Invalidate queries to refresh task list
      queryClient.invalidateQueries({ queryKey: ['tasks'] });

      toast({
        title: 'Task Created',
        description: `"${title}" has been added successfully`
      });

      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create task',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const assignedMember = companyMembers?.find((m) => m.id === assignedTo);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Add New Task</DialogTitle>
        </DialogHeader>
        
        <DialogBody className="space-y-4 px-[10px]">
          {/* Lead Selection */}
          <div className="space-y-2">
            <Label htmlFor="lead" className="required">Lead</Label>
            <Popover open={leadSearchOpen} onOpenChange={setLeadSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={leadSearchOpen}
                  className="w-full justify-between font-normal"
                  disabled={isLoadingLeads}
                >
                  {selectedLeadId
                    ? leads?.find((l) => l.id === selectedLeadId)?.name ?? "Select a lead"
                    : isLoadingLeads ? "Loading leads..." : "Select a lead"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command className="[&_[cmdk-input-wrapper]]:border-border [&_[cmdk-input]]:ring-0 [&_[cmdk-input]]:outline-none">
                  <CommandInput placeholder="Search leads..." />
                  <CommandList>
                    <CommandEmpty>No leads found.</CommandEmpty>
                    <CommandGroup>
                      {leads?.map((lead) => (
                        <CommandItem
                          key={lead.id}
                          value={lead.name}
                          onSelect={() => {
                            setSelectedLeadId(lead.id);
                            setLeadSearchOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", selectedLeadId === lead.id ? "opacity-100" : "opacity-0")} />
                          {lead.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="required">Task Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter task title"
              maxLength={200} />

          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Add task details..." />

          </div>

          {/* Due Date & Time */}
          <div className="space-y-2">
            <Label>Due Date & Time</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 justify-start text-left font-normal",
                      !dueDate && "text-muted-foreground"
                    )}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, "PPP") : "Select due date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={setDueDate}
                    initialFocus
                    className="pointer-events-auto" />
                </PopoverContent>
              </Popover>
              <Input
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className="w-[130px]"
                placeholder="Time"
              />
            </div>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priority</Label>
            <RadioGroup value={priority} onValueChange={setPriority}>
              <div className="flex gap-4">
                {priorityOptions.map((option) =>
                <div key={option.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={option.value} id={`priority-${option.value}`} />
                    <Label htmlFor={`priority-${option.value}`} className="cursor-pointer font-normal">
                      {option.label}
                    </Label>
                  </div>
                )}
              </div>
            </RadioGroup>
          </div>

          {/* Categories */}
          <div className="space-y-2">
            <Label>Categories</Label>
            <TagInput
              selected={categories}
              onChange={setCategories}
              placeholder="Add categories..."
              options={categoryOptions.map((c) => ({ label: c.label, value: c.value }))}
              creatable={true} />

          </div>

          {/* Recurrence */}
          <div className="space-y-2">
            <Label>Recurrence</Label>
            <RecurrenceSelector
              pattern={recurrencePattern}
              interval={recurrenceInterval}
              endDate={recurrenceEndDate}
              onChange={({ pattern, interval, endDate }) => {
                setRecurrencePattern(pattern);
                setRecurrenceInterval(interval);
                setRecurrenceEndDate(endDate);
              }}
            />
          </div>

          {/* Assigned To */}
          <div className="space-y-2">
            <Label htmlFor="assignedTo">Assign To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo} disabled={isLoadingMembers}>
              <SelectTrigger id="assignedTo">
                <SelectValue placeholder={isLoadingMembers ? "Loading members..." : "Select team member"}>
                  {assignedMember &&
                  <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={assignedMember.avatar_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {assignedMember.first_name?.[0]}{assignedMember.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span>{assignedMember.first_name} {assignedMember.last_name}</span>
                    </div>
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {companyMembers && companyMembers.map((member) =>
                <SelectItem key={member.id} value={member.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.avatar_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span>{member.first_name} {member.last_name}</span>
                    </div>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}>

            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedLeadId || !title.trim()}>

            {isSubmitting ?
            <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </> :

            'Create Task'
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);

};

export default AddTaskDialog;